<template>
  <v-card flat class="basket-card">
    <BasketToolbar
      :are-cash-registers-loading="
        areCashRegistersloading || areInvoiceSettingsloading
      "
      :cash-registers="cashRegisters"
      :selected-cash-register-id="selectedCashRegisterId"
      @update:selected-cash-register="setSelectedCashRegister"
      @reset-basket="resetBasket" />
    <div class="basket-columns">
      <div class="basket-column basket-column--left">
        <div class="basket-list-height-helper">
          <BasketList
            :items="cartItems"
            :variants="allCatalogVariants"
            :arena-currency="arenaCurrency"
            :highlighted-cart-item-ids="highlightedCartItemIds"
            :cart="cart"
            @update-variant-quantity="onVariantQuantityChange"
            @update-promotion-quantity="onPromotionQuantityChange"
            @remove-variant="onRemoveVariant"
            @remove-promotion="onRemovePromotion" />
        </div>
        <div class="basket-column__bottom-section">
          <div class="payment-modifiers">
            <CompanyInvoiceCard
              :is-company-invoice="isCompanyInvoiceActive"
              @update:is-company-invoice="handleCompanyInvoiceToggle" />
            <DiscountCard
              :disabled="!isManualDiscountEnabled"
              :does-discount-amount-exceed-subtotal="
                doesDiscountAmountExceedSubtotal
              "
              :discount-amount="discountAmount"
              :is-arena-currency-loading="isArenaCurrencyLoading"
              :is-discount-active="isDiscountActive"
              :max-discount-percentage="maxDiscountPercentage"
              @update:is-discount-active="toggleDiscountState"
              @update:discount-amount="changeDiscountAmount" />
          </div>
          <BasketPaymentMethods
            class="basket-payment"
            :default-method-code="defaultPaymentMethodCode"
            @payment-method-selected="onPaymentMethodSelected" />
        </div>
      </div>
      <div class="basket-column basket-column--right">
        <div v-if="countryCode !== 'ba'" class="recommendations-list-wrapper">
          <RecommendationsList
            ref="recommendationsList"
            :arena-currency="arenaCurrency"
            @highlighted-ids-changed="onHighlightedIdsChanged"
            @recommendation-click="switchItemsWithRecommendation" />
        </div>
        <div class="basket-column__summary-section">
          <PriceSummary
            :arena-currency="arenaCurrency"
            :discount="appliedDiscount"
            :is-cart-loading="isCartLoading"
            :price="basketSubtotal"
            :total="basketTotal"
            class="mb-3" />
          <v-tooltip
            top
            :disabled="!isFinishPaymentDisabled"
            :open-on-hover="isFinishPaymentDisabled"
            :open-on-focus="isFinishPaymentDisabled">
            <template #activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="finish-payment-btn">
                <v-btn
                  color="primary"
                  class="finish-payment-btn"
                  :disabled="isFinishPaymentDisabled"
                  :loading="isInvoiceBeingCreated || isCartLoading"
                  @click="handleFinishPayment">
                  {{ $t('purchaseV2.basket.finishPayment') }}
                </v-btn>
              </span>
            </template>
            <span>{{ finishPaymentDisabledReason }}</span>
          </v-tooltip>
        </div>
      </div>
    </div>
    <CompanyInvoiceDialog
      :company-address="companyInvoiceInfo.address"
      :company-name="companyInvoiceInfo.name"
      :company-pidn="companyInvoiceInfo.pidn"
      :is-opened="isCompanyInvoiceDialogOpen"
      @dialog:close="handleDialogClose"
      @dialog:save="handleDialogSave" />
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import BasketList from './BasketList.vue'
import BasketPaymentMethods from './PaymentMethods.vue'
import BasketToolbar from './BasketToolbar.vue'
import Cart from '@/models/shop/CartV2'
import CashRegister from '@/models/shop/CashRegister'
import CompanyInvoiceCard from './CompanyInvoiceCard.vue'
import CompanyInvoiceDialog from './CompanyInvoiceDialog.vue'
import DiscountCard from './DiscountCard.vue'
import PriceSummary from './PriceSummary.vue'
import RecommendationsList from './RecommendationsList.vue'
import { buildItemsMap, diffNewItems } from '@/utils/basket'
import { COIN_TYPES } from '@/constants/coins'
import { CartItemType } from '@/store/modules/cart'
import Invoice from '@/models/shop/InvoiceV2'
import InvoiceSettings from '@/models/shop/InvoiceSettings'
import printInvoiceApi from '@/mixins/print-invoice-api'
import { PaymentMethodCode } from '@/models/shop/PaymentMethod'

const MAX_DISCOUNT_PERCENTAGE = 100
const MAX_DISCOUNT_PERCENTAGE_BA = 90

const resetCompanyInfo = () => ({
  pidn: '',
  name: '',
  address: '',
})

export default {
  name: 'PurchaseBasket',
  mixins: [printInvoiceApi],
  components: {
    BasketList,
    BasketPaymentMethods,
    BasketToolbar,
    CompanyInvoiceCard,
    CompanyInvoiceDialog,
    DiscountCard,
    PriceSummary,
    RecommendationsList,
  },
  props: {
    arenaCurrency: { type: Object, default: () => ({}) },
    isArenaCurrencyLoading: { type: Boolean, default: false },
  },
  data: () => ({
    areCashRegistersloading: false,
    areInvoiceSettingsloading: false,
    cart: null,
    cashRegisters: [],
    invoiceSettings: null,
    companyInvoiceInfo: resetCompanyInfo(),
    discountAmount: 0,
    highlightedCartItemIds: [],
    isCompanyInvoiceActive: false,
    isCompanyInvoiceDialogOpen: false,
    isDiscountActive: false,
    isInvoiceBeingCreated: false,
    previousVariantItemsMap: new Map(),
    previousPromotionItemsMap: new Map(),
    selectedCashRegisterId: '',
    selectedPaymentMethodCode: '',
    selectedPaymentMethodId: '',
    updatePreviousVariantItemsMap: false,
    updatePreviousPromotionItemsMap: false,
  }),
  computed: {
    ...mapGetters('cart', [
      'cartItems',
      'cartId',
      'allCatalogVariants',
      'isEveryPromotionCompletelySelected',
      'isPromotionSelectionComplete',
      'isCartLoading',
      'selectedCustomerId',
    ]),
    arenaId() {
      return this.$store.getters.arena?.id
    },
    countryCode() {
      return this.$store.getters.country
    },
    maxDiscountPercentage() {
      return this.countryCode === 'ba'
        ? MAX_DISCOUNT_PERCENTAGE_BA
        : MAX_DISCOUNT_PERCENTAGE
    },
    isCashPaymentSelected() {
      return this.selectedPaymentMethodCode === PaymentMethodCode.CSH
    },
    isManualDiscountEnabled() {
      return this.countryCode !== 'ba' || this.isCashPaymentSelected
    },
    defaultPaymentMethodCode() {
      return this.invoiceSettings?.default_payment_method_code || ''
    },
    appliedDiscount() {
      return Number(this.cart?.manual_discount_amount ?? 0)
    },
    basketSubtotal() {
      return Number(this.cart?.total_gross_amount_undiscounted ?? 0)
    },
    basketTotal() {
      return Number(this.cart?.total_gross_amount ?? 0)
    },
    doesDiscountAmountExceedSubtotal() {
      const normalizedDiscount = Number.isFinite(this.discountAmount)
        ? this.discountAmount
        : 0
      return (
        this.isDiscountActive && normalizedDiscount > this.maxDiscountPercentage
      )
    },
    isCompanyInfoFieldValid() {
      if (!this.isCompanyInvoiceActive) return true
      return !!(
        this.companyInvoiceInfo.pidn &&
        this.companyInvoiceInfo.name &&
        this.companyInvoiceInfo.address
      )
    },
    isDiscountFieldValid() {
      return !this.isDiscountActive || !!this.discountAmount
    },
    hasDigitalItemsInCart() {
      const isCoinVariant = (variant) => {
        return !!variant?.attributes?.type
      }
      const isPassVariant = (variant) => {
        return !!variant?.attributes?.weekdays?.length
      }
      const isDigitalVariant = (variant) => {
        return isCoinVariant(variant) || isPassVariant(variant)
      }
      for (const item of this.cartItems) {
        if (item.type === 'variant' && item.variant) {
          if (isDigitalVariant(item.variant)) return true
        }
        if (item.type === 'promotion' && item.promotion?.groups) {
          for (const group of item.promotion.groups) {
            for (const gv of group.variants || []) {
              const variant = this.allCatalogVariants.find(
                (v) => String(v?.id) === String(gv.arena_variant_id),
              )
              if (variant && isDigitalVariant(variant)) return true
            }
          }
        }
      }
      return false
    },
    hasDigitalItemsWithoutCustomer() {
      return this.hasDigitalItemsInCart && !this.selectedCustomerId
    },
    isFinishPaymentDisabled() {
      return (
        !this.isSingleItemSelected ||
        this.hasDigitalItemsWithoutCustomer ||
        !this.isCompanyInfoFieldValid ||
        !this.isDiscountFieldValid ||
        this.doesDiscountAmountExceedSubtotal ||
        !this.isEveryPromotionCompletelySelected
      )
    },
    isSingleItemSelected() {
      return !!this.cartItems.length
    },
    finishPaymentDisabledReason() {
      if (!this.isSingleItemSelected) {
        return this.$t('purchaseV2.basket.paymentValidation.noItems')
      }
      if (this.hasDigitalItemsWithoutCustomer) {
        return this.$t(
          'purchaseV2.basket.paymentValidation.digitalItemRequiresCustomer',
        )
      }
      if (!this.isCompanyInfoFieldValid) {
        return this.$t('purchaseV2.basket.paymentValidation.invalidCompanyInfo')
      }
      if (!this.isDiscountFieldValid) {
        return this.$t(
          'purchaseV2.basket.paymentValidation.invalidDiscountField',
        )
      }
      if (this.doesDiscountAmountExceedSubtotal) {
        return this.$t(
          'purchaseV2.basket.paymentValidation.discountExceedsMaxPercentage',
          { maxPct: this.maxDiscountPercentage },
        )
      }
      if (!this.isEveryPromotionCompletelySelected) {
        return this.$t(
          'purchaseV2.basket.paymentValidation.incompletePromotionSelection',
        )
      }
      return ''
    },
  },
  methods: {
    onHighlightedIdsChanged(ids) {
      this.highlightedCartItemIds = ids || []
    },
    changeDiscountAmount(amount) {
      this.discountAmount = amount
      this.persistDiscountChange()
    },
    setSelectedCashRegister(id) {
      this.selectedCashRegisterId = id
    },
    async hydrateExistingCart() {
      // If the cart store is already populated when this component mounts (e.g.
      // state restored from a previous view), the local `cart` object and the
      // diff baselines (`previous*ItemsMap`) start empty and out of sync with
      // the store's `cartItems`. That desync means a subsequent remove diffs to
      // nothing and never hits the API, leaving a stale total on screen. Pull
      // the server cart so totals render, then seed the baselines so the next
      // add/remove reconciles correctly.
      if (this.arenaId && this.cartId) {
        this.$store.dispatch('cart/setCartLoading', true)
        try {
          const { data = {} } = await Cart.api.get(this.arenaId, this.cartId)
          this.setCart(new Cart(data))
        } catch (error) {
          console.warn('Hydrating existing cart failed', error)
        } finally {
          this.$store.dispatch('cart/setCartLoading', false)
        }
      }
      this.seedPreviousItemsMaps()
    },
    seedPreviousItemsMaps() {
      const variantItems = this.cartItems
        .filter((item) => item.type === 'variant')
        .map((item) => ({ variant: item.variant, quantity: item.quantity }))
      this.previousVariantItemsMap = buildItemsMap(variantItems)

      const promotionItems = this.cartItems.filter(
        (item) => item.type === 'promotion',
      )
      this.previousPromotionItemsMap = new Map(
        promotionItems
          .filter((item) =>
            this.isPromotionSelectionComplete(item.promotion.id),
          )
          .map((item) => [
            String(item.promotion.id),
            {
              promotion: item.promotion,
              quantity: item.quantity,
              groupSelections: item.groupSelections || {},
            },
          ]),
      )
    },
    async createCart(payload = {}) {
      if (!this.arenaId || !this.arenaCurrency?.id) return
      this.$store.dispatch('cart/setCartLoading', true)
      try {
        const { data = {} } = await Cart.api.create(this.arenaId, {
          currency_id: this.arenaCurrency.id,
          ...payload,
        })
        const cart = new Cart(data)
        this.setCart(cart)
        return cart
      } catch (error) {
        console.error('Creating cart failed', error)
        this.cart = null
      } finally {
        this.$store.dispatch('cart/setCartLoading', false)
      }
    },
    async fetchCashRegisters() {
      if (!this.arenaId) return
      this.areCashRegistersloading = true
      try {
        const params = 'page=1&size=100'
        const response = await CashRegister.api.list(this.arenaId, params)
        const data = response.data?.data || []
        this.cashRegisters = data.map((it) => new CashRegister(it))
        const defaultCashRegister = this.cashRegisters[0]
        this.selectedCashRegisterId = defaultCashRegister?.id
      } catch (error) {
        console.warn(error)
      } finally {
        this.areCashRegistersloading = false
      }
    },
    async fetchInvoiceSettings() {
      if (!this.arenaId) return
      this.areInvoiceSettingsloading = true
      try {
        const response = await Invoice.api.getArenaInvoiceSettings(this.arenaId)
        this.invoiceSettings = new InvoiceSettings(response.data)
      } catch (error) {
        console.warn(error)
      } finally {
        this.areInvoiceSettingsloading = false
      }
    },
    handleCompanyInvoiceToggle(isActive) {
      if (isActive) {
        this.isCompanyInvoiceActive = true
        this.isCompanyInvoiceDialogOpen = true
      } else {
        this.isCompanyInvoiceActive = false
        this.companyInvoiceInfo = resetCompanyInfo()
      }
    },
    handleDialogClose() {
      this.isCompanyInvoiceDialogOpen = false
      this.isCompanyInvoiceActive = false
    },
    handleDialogSave(data) {
      this.companyInvoiceInfo = {
        name: data.name,
        pidn: data.pidn,
        address: data.address,
      }
      this.isCompanyInvoiceActive = true
      this.isCompanyInvoiceDialogOpen = false
    },
    toggleDiscountState(isActive) {
      this.isDiscountActive = isActive
      if (!isActive) this.changeDiscountAmount(0)
    },
    onVariantQuantityChange(payload) {
      this.$store.dispatch('cart/updateVariantQuantity', payload)
    },
    onPromotionQuantityChange(payload) {
      this.$store.dispatch('cart/updatePromotionQuantity', payload)
    },
    onRemoveVariant({ variant }) {
      this.$store.dispatch('cart/removeVariant', variant)
    },
    async onRemovePromotion({ promotion }) {
      this.$store.dispatch('cart/removePromotion', promotion)
    },
    onPaymentMethodSelected(method) {
      const previousMethodId = this.selectedPaymentMethodId
      this.selectedPaymentMethodId = method?.id || ''
      this.selectedPaymentMethodCode = method?.code || ''
      // In BA arenas, changing the payment method resets the manual discount,
      // as if the seller had switched it off
      if (
        this.countryCode !== 'ba' ||
        !previousMethodId ||
        previousMethodId === this.selectedPaymentMethodId
      )
        return
      const hadPersistedDiscount = this.appliedDiscount > 0
      this.resetDiscount()
      if (hadPersistedDiscount && this.cartId) this.persistDiscountChange()
    },
    resetBasket() {
      this.$emit('reset-basket')
    },
    resetDiscount() {
      this.discountAmount = 0
      this.isDiscountActive = false
    },
    resetCart() {
      this.cart = null
    },
    getVariantUnitPrice(variant) {
      const priceInfo = variant?.prices?.[0]
      const price =
        priceInfo?.gross_price ?? priceInfo?.price ?? variant?.price ?? 0
      const parsed = Number(price)
      return Number.isFinite(parsed) ? parsed : 0
    },
    setCart(cart) {
      this.cart = cart
      this.$store.commit('cart/setCartId', cart.id)
    },
    getCartItemIdByVariant(variant) {
      const cartItem = this.cart?.items.find(
        (it) => it.arena_variant_id === variant.id,
      )
      return cartItem?.id
    },
    getCartItemIdByPromotion(promotion) {
      const cartItem = this.cart?.items.find(
        (it) => it.arena_promotion_id === promotion.id,
      )
      return cartItem?.id
    },
    buildPromotionAddItemPayload(
      promotion,
      groupSelections = {},
      variants = [],
    ) {
      const extractVariantId = (selection) =>
        typeof selection === 'object' ? selection?.id : selection

      const isCoinsGroup = (group) => {
        if (!group?.variants?.length || !variants.length) return false
        const groupVariantIds = new Set(
          group.variants.map((gv) => gv.arena_variant_id),
        )
        return variants.some(
          (variant) =>
            groupVariantIds.has(variant.id) &&
            variant?.attributes?.type === COIN_TYPES.coins,
        )
      }

      const buildGroupPayload = (group) => {
        const selectedVariants = groupSelections[group.id]
        if (!selectedVariants) return null

        const variantsArray = Array.isArray(selectedVariants)
          ? selectedVariants
          : [selectedVariants]

        const isCoins = isCoinsGroup(group)

        if (isCoins) {
          const firstValid = variantsArray.find((sel) => !!sel)
          const variantId = firstValid ? extractVariantId(firstValid) : null
          if (!variantId) return null

          return {
            arena_promotion_group_id: group.id,
            variants: [
              {
                arena_variant_id: String(variantId),
                quantity: Number(group.quantity) || 1,
              },
            ],
          }
        }

        const variantMap = new Map()
        variantsArray.forEach((selection) => {
          const variantId = extractVariantId(selection)
          if (variantId) {
            const id = String(variantId)
            variantMap.set(id, (variantMap.get(id) || 0) + 1)
          }
        })

        if (!variantMap.size) return null

        return {
          arena_promotion_group_id: group.id,
          variants: Array.from(variantMap.entries()).map(
            ([arena_variant_id, quantity]) => ({
              arena_variant_id,
              quantity,
            }),
          ),
        }
      }

      const groups = promotion?.groups?.map(buildGroupPayload).filter(Boolean)

      if (!groups?.length) return null

      return {
        arena_promotion: {
          arena_promotion_id: promotion.id,
          groups,
        },
      }
    },
    /**
     * Parses backend insufficient variant stock error.
     * Message format: "cannot add promotion to cart: insufficient variant stock available: `{uuid}`. available: X, required: Y"
     * @returns {{ arenaVariantId: string, available: number, required: number } | null}
     */
    parseInsufficientVariantStockError(error) {
      const message = error?.response?.data?.message || error?.message || ''
      const variantIdMatch = message.match(/`([a-f0-9-]{36})`/i)
      const stockMatch = message.match(
        /available:\s*(\d+),\s*required:\s*(\d+)/i,
      )
      if (!variantIdMatch || !stockMatch) return null
      return {
        arenaVariantId: variantIdMatch[1],
        available: parseInt(stockMatch[1], 10),
        required: parseInt(stockMatch[2], 10),
      }
    },
    /**
     * Returns new groupSelections with the given arena_variant_id cleared (set to null).
     */
    clearVariantFromPromotionSelections(promotionItem, arenaVariantId) {
      const idStr = String(arenaVariantId)
      const nextSelections = { ...(promotionItem.groupSelections || {}) }
      for (const group of promotionItem.promotion?.groups || []) {
        const arr = nextSelections[group.id]
        if (!Array.isArray(arr)) continue
        nextSelections[group.id] = arr.map((sel) => {
          if (!sel) return null
          const selId = sel?.id != null ? String(sel.id) : ''
          return selId === idStr ? null : sel
        })
      }
      return nextSelections
    },
    arePromotionSelectionsEqual(a = {}, b = {}) {
      const normalizeValue = (value) => {
        if (!value) return ''
        return typeof value === 'object'
          ? String(value.id ?? value.value ?? '')
          : String(value)
      }

      const normalizeSelections = (selections = {}) =>
        Object.entries(selections).reduce((acc, [groupId, selected]) => {
          // Always expect array format - convert single value if needed (backward compatibility)
          const selectedArray = Array.isArray(selected) ? selected : [selected]
          const normalized = selectedArray
            .map(normalizeValue)
            .filter((v) => v)
            .sort()
            .join(',')
          acc[groupId] = normalized
          return acc
        }, {})

      const normalizedA = normalizeSelections(a)
      const normalizedB = normalizeSelections(b)
      const allKeys = new Set([
        ...Object.keys(normalizedA),
        ...Object.keys(normalizedB),
      ])

      return Array.from(allKeys).every(
        (key) => normalizedA[key] === normalizedB[key],
      )
    },
    async addItemToCart(variant, quantity) {
      this.$store.dispatch('cart/setCartLoading', true)
      try {
        if (!this.cartId) {
          const cart = await this.createCart()
          if (!cart) throw new Error('Failed to create cart')
        }
        const payload = {
          arena_variant: {
            arena_variant_id: variant.id,
            quantity,
          },
        }
        const { data = {} } = await Cart.api.addItemToCart(
          this.arenaId,
          this.cartId,
          payload,
        )
        const cart = new Cart(data)
        this.setCart(cart)
        this.updatePreviousVariantItemsMap = true
      } catch (error) {
        this.updatePreviousVariantItemsMap = false
        console.error('Adding item to cart failed', error)
        const message = this.getErrorMessage(error)

        this.$store.dispatch('cart/rollbackCart')

        this.$store.dispatch('showSnackbar', {
          message: message,
          color: 'error',
          icon: 'error',
        })
      } finally {
        this.$store.dispatch('cart/clearSnapshot')
        this.$store.dispatch('cart/setCartLoading', false)
      }
    },
    async addPromotionToCart(promotionItem) {
      if (!promotionItem?.promotion) return
      if (!this.cartId) await this.createCart()
      const payload = this.buildPromotionAddItemPayload(
        promotionItem.promotion,
        promotionItem.groupSelections,
        this.allCatalogVariants,
      )
      if (!payload) return

      this.$store.dispatch('cart/setCartLoading', true)
      try {
        const { data = {} } = await Cart.api.addItemToCart(
          this.arenaId,
          this.cartId,
          payload,
        )
        const cart = new Cart(data)
        this.setCart(cart)
        this.updatePreviousPromotionItemsMap = true
      } catch (error) {
        console.error('Adding promotion to cart failed', error)
        this.updatePreviousPromotionItemsMap = false
        const parsed = this.parseInsufficientVariantStockError(error)
        if (parsed) {
          const { arenaVariantId, available } = parsed
          const clearedSelections = this.clearVariantFromPromotionSelections(
            promotionItem,
            arenaVariantId,
          )
          this.$store.dispatch('cart/setPromotionSelection', {
            promotion: promotionItem.promotion,
            selections: clearedSelections,
          })
          const variant = this.allCatalogVariants.find(
            (v) => String(v?.id) === String(arenaVariantId),
          )
          const variantName = variant?.name ?? arenaVariantId
          this.$store.dispatch('showSnackbar', {
            color: 'error',
            message: this.$t('purchaseV2.basket.insufficientVariantStock', {
              variantName,
              availableStock: available,
            }),
            icon: 'error',
          })
        } else {
          this.$store.dispatch('cart/rollbackCart')
          this.$store.dispatch('cart/removePromotion', promotionItem.promotion)
          this.$store.dispatch('showSnackbar', {
            message: this.getErrorMessage(error),
            color: 'error',
            icon: 'error',
          })
        }
      } finally {
        this.$store.dispatch('cart/clearSnapshot')
        this.$store.dispatch('cart/setCartLoading', false)
      }
    },
    async setItemQuantity(variant, quantity) {
      if (!this.arenaId || !this.cartId || !variant) return
      const itemId = this.getCartItemIdByVariant(variant)
      if (!itemId) return
      this.$store.dispatch('cart/setCartLoading', true)
      try {
        const { data = {} } = await Cart.api.setItemQuantity(
          this.arenaId,
          this.cartId,
          itemId,
          { quantity },
        )
        const cart = new Cart(data)
        this.setCart(cart)
        this.updatePreviousVariantItemsMap = true
      } catch (error) {
        this.updatePreviousVariantItemsMap = false
        console.error('Updating item quantity failed', error)
        const message = this.getErrorMessage(error)
        this.$store.dispatch('cart/rollbackCart')
        this.$store.dispatch('showSnackbar', {
          message: message,
          color: 'error',
          icon: 'error',
        })
      } finally {
        this.$store.dispatch('cart/clearSnapshot')
        this.$store.dispatch('cart/setCartLoading', false)
      }
    },
    async setPromotionQuantity(promotion, quantity) {
      if (!this.arenaId || !this.cartId || !promotion) return
      const itemId = this.getCartItemIdByPromotion(promotion)
      if (!itemId) return
      this.$store.dispatch('cart/setCartLoading', true)
      try {
        const { data = {} } = await Cart.api.setItemQuantity(
          this.arenaId,
          this.cartId,
          itemId,
          { quantity },
        )
        const cart = new Cart(data)
        this.setCart(cart)
        this.updatePreviousPromotionItemsMap = true
      } catch (error) {
        console.error('Updating promotion quantity failed', error)
        this.updatePreviousPromotionItemsMap = false
        this.$store.dispatch('cart/rollbackCart')
        this.$store.dispatch('showSnackbar', {
          message: this.getErrorMessage(error),
          color: 'error',
          icon: 'error',
        })
      } finally {
        this.$store.dispatch('cart/clearSnapshot')
        this.$store.dispatch('cart/setCartLoading', false)
      }
    },
    async removeItemFromCart(variant) {
      if (!this.arenaId || !this.cartId || !variant) return
      const itemId = this.getCartItemIdByVariant(variant)
      if (!itemId) return
      this.$store.dispatch('cart/setCartLoading', true)
      try {
        const { data = {} } = await Cart.api.removeItemFromCart(
          this.arenaId,
          this.cartId,
          itemId,
        )
        const cart = new Cart(data)
        this.setCart(cart)
        this.updatePreviousVariantItemsMap = true
      } catch (error) {
        this.updatePreviousVariantItemsMap = false
        console.error('Removing item from cart failed', error)
        const message = this.getErrorMessage(error)
        this.$store.dispatch('cart/rollbackCart')

        this.$store.dispatch('showSnackbar', {
          message: message,
          color: 'error',
          icon: 'error',
        })
      } finally {
        this.$store.dispatch('cart/clearSnapshot')
        this.$store.dispatch('cart/setCartLoading', false)
      }
    },
    async removePromotionFromCart(promotion) {
      if (!this.arenaId || !this.cartId || !promotion) return
      // In current implementation there can be only 1 promotion instance in the
      // basket. That is why searching only by arena_promotion_id is allowed.
      // When multiple promotion instances are allowed as basket items, this
      // needs to changed.
      const promotionCartItem = this.cart?.items?.find(
        (it) => it.arena_promotion_id === promotion.id,
      )
      if (!promotionCartItem) return
      this.$store.dispatch('cart/setCartLoading', true)
      try {
        const { data = {} } = await Cart.api.removePromotionFromCart(
          this.arenaId,
          this.cartId,
          promotion.id,
          promotionCartItem.promotion_instance,
        )
        const cart = new Cart(data)
        this.setCart(cart)
        this.updatePreviousPromotionItemsMap = true
      } catch (error) {
        console.error('Removing promotion from cart failed', error)
        this.updatePreviousPromotionItemsMap = false
        this.$store.dispatch('cart/rollbackCart')
        this.$store.dispatch('showSnackbar', {
          message: this.getErrorMessage(error),
          color: 'error',
          icon: 'error',
        })
      } finally {
        this.$store.dispatch('cart/clearSnapshot')
        this.$store.dispatch('cart/setCartLoading', false)
      }
    },
    async handleItemsChange(newItems) {
      await this.syncVariantItemsWithCart(newItems)
      await this.syncPromotionItemsWithCart(newItems)
      await this.refreshPromotions()
    },
    async syncVariantItemsWithCart(newItems) {
      const variantItems = newItems.filter((item) => item.type === 'variant')
      const oldMap = this.previousVariantItemsMap || new Map()
      const itemsForMap = variantItems.map((item) => ({
        variant: item.variant,
        quantity: item.quantity,
      }))
      const newMap = buildItemsMap(itemsForMap)

      const { additions, updates, removals } = diffNewItems(oldMap, newMap)

      if (!additions.length && !updates.length && !removals.length) return

      await this.handleItemAdditions(additions)
      await this.handleItemUpdates(updates)
      await this.handleItemRemovals(removals)
      if (this.updatePreviousVariantItemsMap) {
        this.previousVariantItemsMap = newMap
      }
    },
    async syncPromotionItemsWithCart(newItems = this.cartItems) {
      if (!this.arenaId) return
      const promotionItems = newItems.filter(
        (item) => item.type === 'promotion',
      )
      const oldMap = this.previousPromotionItemsMap || new Map()

      const completePromotionItems = promotionItems.filter((item) =>
        this.isPromotionSelectionComplete(item.promotion.id),
      )

      const newMap = new Map(
        completePromotionItems.map((item) => [
          String(item.promotion.id),
          {
            promotion: item.promotion,
            quantity: item.quantity,
            groupSelections: item.groupSelections || {},
          },
        ]),
      )

      const additions = []
      const updates = []
      const removals = []

      for (const [promotionId, item] of newMap) {
        const previous = oldMap.get(promotionId)
        if (!previous) {
          additions.push(item)
          continue
        }
        const selectionsChanged = !this.arePromotionSelectionsEqual(
          previous.groupSelections,
          item.groupSelections,
        )
        const quantityChanged = previous.quantity !== item.quantity
        if (selectionsChanged || quantityChanged) {
          updates.push({ ...item, selectionsChanged })
        }
      }

      for (const [promotionId, item] of oldMap) {
        if (!newMap.has(promotionId)) {
          removals.push(item)
        }
      }

      if (!additions.length && !updates.length && !removals.length) {
        return
      }

      await this.handlePromotionRemovals(removals)
      await this.handlePromotionUpdates(updates)
      await this.handlePromotionAdditions(additions)

      if (this.updatePreviousPromotionItemsMap) {
        this.previousPromotionItemsMap = newMap
      }
    },
    async handleItemAdditions(additions) {
      for (const item of additions) {
        await this.addItemToCart(item.variant, item.quantity)
      }
    },
    async handleItemUpdates(updates) {
      for (const item of updates) {
        await this.setItemQuantity(item.variant, item.quantity)
      }
    },
    async handleItemRemovals(removals) {
      for (const item of removals) {
        await this.removeItemFromCart(item.variant)
      }
    },
    async handlePromotionAdditions(additions) {
      for (const item of additions) {
        await this.addPromotionToCart(item)
      }
    },
    async handlePromotionUpdates(updates) {
      for (const item of updates) {
        if (item.selectionsChanged) {
          await this.removePromotionFromCart(item.promotion)
          await this.addPromotionToCart(item)
        } else {
          await this.setPromotionQuantity(item.promotion, item.quantity)
        }
      }
    },
    async handlePromotionRemovals(removals) {
      for (const item of removals) {
        await this.removePromotionFromCart(item.promotion)
      }
    },
    async updateCart(payload) {
      if (!this.arenaId || !this.cartId) return
      this.$store.dispatch('cart/setCartLoading', true)
      try {
        const { data = {} } = await Cart.api.update(
          this.arenaId,
          this.cartId,
          payload,
        )
        const cart = new Cart(data)
        this.cart = cart
      } catch (error) {
        console.error('Updating cart failed', error)
      } finally {
        this.$store.dispatch('cart/setCartLoading', false)
      }
    },
    async handleFinishPayment() {
      if (
        !this.arenaId ||
        !this.selectedPaymentMethodId ||
        !this.selectedCashRegisterId
      )
        return
      this.isInvoiceBeingCreated = true
      try {
        const payload = {
          payment_method_id: this.selectedPaymentMethodId,
          cash_register_id: this.selectedCashRegisterId,
        }
        if (this.isCompanyInvoiceActive) {
          payload.b2b_data = {
            legal_name: this.companyInvoiceInfo.name,
            tax_number: this.companyInvoiceInfo.pidn,
            address: this.companyInvoiceInfo.address,
          }
        }
        const response = await Cart.api.createInvoice(
          this.arenaId,
          this.cartId,
          payload,
        )
        const response_data = response.data

        const invoice = response_data.invoice
          ? new Invoice(response_data.invoice)
          : null

        if (
          invoice &&
          this.invoiceSettings &&
          this.invoiceSettings.automatically_open_print_upon_invoice_creation &&
          invoice.invoice_file_name
        ) {
          await this.printInvoice(response_data.invoice)
        }

        await this.resetAfterCheckout()
        this.$emit('payment-success')
        if (
          invoice &&
          invoice.fiscalization_required &&
          this.countryCode !== 'ba'
        ) {
          this.$store.dispatch('showSnackbar', {
            message: this.$t('purchase.invoiceCreatedButNotFiscalized'),
            color: 'warning',
            icon: 'check_circle',
          })
        } else {
          this.$store.dispatch('showSnackbar', {
            message: this.$t('purchaseV2.basket.purchaseCompleted'),
            color: 'success',
            icon: 'check_circle',
          })
        }
      } catch (error) {
        console.error('Creating invoice failed', error)
        const errorMessage =
          error?.response?.data?.message || this.$t('errors.default')
        this.$store.dispatch('showSnackbar', {
          message: errorMessage,
          color: 'error',
          icon: 'error',
        })
      } finally {
        this.isInvoiceBeingCreated = false
      }
    },
    async persistDiscountChange() {
      const normalized = Number.isFinite(this.discountAmount)
        ? this.discountAmount
        : 0
      const clamped = Math.min(
        Math.max(normalized, 0),
        this.maxDiscountPercentage,
      )
      const payload = { manual_discount_amount: clamped.toFixed(2) }
      if (this.cartId) {
        await this.updateCart(payload)
      } else {
        await this.createCart(payload)
      }
    },
    async refreshPromotions() {
      if (this.$refs.recommendationsList?.fetchPromotions) {
        await this.$refs.recommendationsList.fetchPromotions()
      }
    },
    async resetAfterCheckout() {
      await this.$store.dispatch('cart/resetCart')
      this.cart = null
      this.previousVariantItemsMap = new Map()
      this.previousPromotionItemsMap = new Map()
      this.discountAmount = 0
      this.isDiscountActive = false
      this.isCompanyInvoiceActive = false
      this.companyInvoiceInfo = resetCompanyInfo()
      // Clear recommendations when cart is reset
      this.refreshPromotions()
    },
    async switchItemsWithRecommendation(recommendation) {
      this.$store.dispatch('cart/setSelectedRecommendation', recommendation)

      const cartItemsToUpdate = []
      if (recommendation.existing_items) {
        for (const existingItem of recommendation.existing_items) {
          if (existingItem.cart_item_id) {
            const cartItem = this.cart?.items.find(
              (it) => it.id === existingItem.cart_item_id,
            )
            if (cartItem) {
              cartItemsToUpdate.push({
                cartItem,
                existingItem,
              })
            }
          }
        }
      }

      const variantItems = this.cartItems.filter(
        (item) => item.type === CartItemType.VARIANT,
      )
      const itemsForMap = variantItems.map((item) => ({
        variant: item.variant,
        quantity: item.quantity,
      }))
      this.previousVariantItemsMap = buildItemsMap(itemsForMap)

      for (const { cartItem, existingItem } of cartItemsToUpdate) {
        const currentQuantity = cartItem.quantity
        const neededQuantity = existingItem.quantity
        const newQuantity = currentQuantity - neededQuantity

        const variant = this.allCatalogVariants.find(
          (v) => v.id === existingItem.arena_variant.id,
        )
        if (!variant) continue

        if (newQuantity > 0) {
          this.$store.dispatch('cart/updateVariantQuantity', {
            variant,
            quantity: newQuantity,
          })
          this.previousVariantItemsMap.set(variant.id, {
            variant,
            quantity: newQuantity,
          })
          await this.setItemQuantity(variant, newQuantity)
        } else {
          this.$store.commit('cart/removeCartVariantByVariantId', variant.id)
          this.previousVariantItemsMap.delete(variant.id)
          await this.removeItemFromCart(variant)
        }
      }

      // Build promotion payload from existing_items and missing_items
      const allItems = [
        ...(recommendation.existing_items || []),
        ...(recommendation.missing_items || []),
      ]

      // Group variants by promotion group
      const promotion = recommendation.arena_promotion
      if (!promotion || !promotion.groups) {
        console.error('Invalid promotion structure')
        return
      }

      // Build group selections from items
      const groupVariantMap = new Map()
      for (const item of allItems) {
        const variantId = item.arena_variant.id
        const quantity = item.quantity

        // Find which group this variant belongs to
        for (const group of promotion.groups) {
          const belongsToGroup = group.variants?.some(
            (gv) => gv.arena_variant_id === variantId,
          )
          if (belongsToGroup) {
            if (!groupVariantMap.has(group.id)) {
              groupVariantMap.set(group.id, new Map())
            }
            const variantMap = groupVariantMap.get(group.id)
            variantMap.set(
              variantId,
              (variantMap.get(variantId) || 0) + quantity,
            )
            break
          }
        }
      }

      // Build groups payload
      const groups = []
      for (const [groupId, variantMap] of groupVariantMap.entries()) {
        if (variantMap.size > 0) {
          groups.push({
            arena_promotion_group_id: groupId,
            variants: Array.from(variantMap.entries()).map(
              ([arena_variant_id, quantity]) => ({
                arena_variant_id: String(arena_variant_id),
                quantity: Number(quantity),
              }),
            ),
          })
        }
      }

      if (groups.length === 0) {
        console.error('No valid groups found for recommendation')
        return
      }

      // Build payload
      const payload = {
        arena_promotion: {
          arena_promotion_id: promotion.id,
          groups,
        },
      }

      // Call AddItemToCart API
      if (!this.cartId) await this.createCart()
      this.$store.dispatch('cart/setCartLoading', true)
      try {
        const { data = {} } = await Cart.api.addItemToCart(
          this.arenaId,
          this.cartId,
          payload,
        )
        const cart = new Cart(data)
        this.setCart(cart)

        // Add promotion with preselected elements
        // Build groupSelections for the promotion
        const groupSelections = {}
        for (const [groupId, variantMap] of groupVariantMap.entries()) {
          const selections = []
          for (const [variantId, quantity] of variantMap.entries()) {
            const variant = this.allCatalogVariants.find(
              (v) => v.id === variantId,
            )
            if (variant) {
              const price = variant.prices?.[0]
              // Add variant selection multiple times based on quantity
              for (let i = 0; i < quantity; i++) {
                selections.push({
                  id: variantId,
                  name: variant.name,
                  image: variant.image,
                  price: price?.gross_price || '0',
                  currency:
                    price?.currency?.symbol ||
                    price?.currency?.code ||
                    this.arenaCurrency?.symbol ||
                    this.arenaCurrency?.code ||
                    '',
                })
              }
            }
          }
          if (selections.length > 0) {
            groupSelections[groupId] = selections
          }
        }

        // Add promotion to cartItems
        this.$store.commit('cart/addCartItem', {
          type: CartItemType.PROMOTION,
          promotion,
          quantity: 1,
          groupSelections,
        })

        // Update previousPromotionItemsMap to prevent watcher from making duplicate API call
        // The watcher will see this promotion as already synced
        if (!this.previousPromotionItemsMap) {
          this.previousPromotionItemsMap = new Map()
        }
        this.previousPromotionItemsMap.set(String(promotion.id), {
          promotion,
          quantity: 1,
          groupSelections,
        })
      } catch (error) {
        console.error('Adding recommendation to cart failed', error)
      } finally {
        this.$store.dispatch('cart/setCartLoading', false)
      }
    },

    getErrorMessage(error) {
      const message = error.response?.data?.message || error.message

      let errorMessage = this.$t('errors.default')
      if (Array.isArray(message)) {
        // If message is an array, extract 'msg' from each item
        errorMessage = message.map((item) => item.msg || item).join(', ')
      } else if (typeof message === 'string') {
        // If message is a string, use it directly
        errorMessage = message
      }
      return errorMessage
    },
  },
  watch: {
    cartItems: {
      async handler(newItems) {
        await this.handleItemsChange(newItems)
      },
      deep: true,
    },
  },
  async created() {
    await Promise.all([this.fetchCashRegisters(), this.fetchInvoiceSettings()])
    await this.hydrateExistingCart()
  },
}
</script>

<style scoped>
.basket-card {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  box-sizing: border-box;
}

.basket-columns {
  display: grid;
  grid-template-columns: 65fr 35fr;
  column-gap: 1rem;
  flex: 1 1 auto;
  min-height: 0;
}

.basket-column {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.basket-list-height-helper {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-bottom: 25px;
}

.basket-column--left .basket-column__bottom-section {
  display: flex;
  flex-direction: column;
  margin-top: auto;
}

.payment-modifiers {
  display: flex;
  gap: 0.25rem;
}

.recommendations-list-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  margin-bottom: 3px;
}

.basket-column__summary-section {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  margin-top: auto;
}

.finish-payment-btn {
  border-radius: 8px;
  width: 100%;
  max-width: 18.75rem;
  height: 4.75rem !important;
  font-size: 1.25rem !important;
}
</style>
